/**
 * Pure helpers for the live-preview filter panel. The UI sliders own
 * `FiltersState` in React state and call `filtersToCss(state)` to derive
 * the CSS filter string applied to the canvas container — keeping the
 * composition logic outside React makes it trivially testable.
 *
 * All filters are client-side previews. None of them mutate the
 * underlying asset; the user has to explicitly "bake" via an operation
 * (Enhance / ColorCorrect / etc.) for the change to persist.
 */

export interface FiltersState {
  /** 100 = neutral. Range [0, 200]. */
  brightness: number;
  /** 100 = neutral. Range [0, 200]. */
  contrast: number;
  /** 100 = neutral. 0 = grayscale-ish, 200 = highly saturated. */
  saturation: number;
  /** 0 = neutral. Range [-180, 180] degrees. */
  hue: number;
  /** 0 = no blur. Range [0, 10] px. */
  blur: number;
  /** 0 = original. 100 = full grayscale. */
  grayscale: number;
  /** 0 = original. 100 = full sepia. */
  sepia: number;
  /** 0 = original. 100 = fully inverted. */
  invert: number;
}

export const DEFAULT_FILTERS_STATE: FiltersState = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hue: 0,
  blur: 0,
  grayscale: 0,
  sepia: 0,
  invert: 0,
};

/**
 * Compose the canvas CSS `filter` string from the current state.
 * Returns `'none'` when every dimension is at its neutral value so the
 * browser can skip the GPU pass entirely.
 *
 * Order is intentional — the CSS spec applies filters left-to-right,
 * and `brightness → contrast → saturation` before `hue-rotate` then
 * tonal shifts (grayscale / sepia / invert) before the final spatial
 * `blur` matches how editors stack adjustments.
 */
export function filtersToCss(state: FiltersState): string {
  if (isDefault(state)) return 'none';
  const parts: string[] = [];
  if (state.brightness !== 100) parts.push(`brightness(${state.brightness}%)`);
  if (state.contrast !== 100) parts.push(`contrast(${state.contrast}%)`);
  if (state.saturation !== 100) parts.push(`saturate(${state.saturation}%)`);
  if (state.hue !== 0) parts.push(`hue-rotate(${state.hue}deg)`);
  if (state.grayscale !== 0) parts.push(`grayscale(${state.grayscale}%)`);
  if (state.sepia !== 0) parts.push(`sepia(${state.sepia}%)`);
  if (state.invert !== 0) parts.push(`invert(${state.invert}%)`);
  if (state.blur !== 0) parts.push(`blur(${state.blur}px)`);
  return parts.length === 0 ? 'none' : parts.join(' ');
}

/** True when every dimension is at its neutral value. */
export function isDefault(state: FiltersState): boolean {
  return (
    state.brightness === 100 &&
    state.contrast === 100 &&
    state.saturation === 100 &&
    state.hue === 0 &&
    state.blur === 0 &&
    state.grayscale === 0 &&
    state.sepia === 0 &&
    state.invert === 0
  );
}

/** Reset to the neutral baseline. Convenience over `DEFAULT_FILTERS_STATE` literal. */
export function resetFilters(): FiltersState {
  return { ...DEFAULT_FILTERS_STATE };
}

/**
 * Compact summary of the active adjustments — used as a tooltip on the
 * preview state ("brightness 120% · saturation 80% · sepia 30%").
 * Returns empty string when nothing's adjusted.
 */
export function describeFilters(state: FiltersState): string {
  if (isDefault(state)) return '';
  const parts: string[] = [];
  if (state.brightness !== 100) parts.push(`brightness ${state.brightness}%`);
  if (state.contrast !== 100) parts.push(`contrast ${state.contrast}%`);
  if (state.saturation !== 100) parts.push(`saturation ${state.saturation}%`);
  if (state.hue !== 0) parts.push(`hue ${state.hue > 0 ? '+' : ''}${state.hue}°`);
  if (state.blur !== 0) parts.push(`blur ${state.blur}px`);
  if (state.grayscale !== 0) parts.push(`grayscale ${state.grayscale}%`);
  if (state.sepia !== 0) parts.push(`sepia ${state.sepia}%`);
  if (state.invert !== 0) parts.push(`invert ${state.invert}%`);
  return parts.join(' · ');
}
